import useStateMachine from '@cassiozen/usestatemachine';
import { TextInput } from 'react-native';

export type UseAutoCompleteInputEventOrchestrationArgs = {
    onBlurFromParent?: () => void;
    onFocusFromParent?: () => void;
    textInput: TextInput | null;
};

export type UseAutoCompleteInputEventOrchestrationReturnValue = {
    onInputFocus: () => void;
    onInputBlur: () => void;
    onPressOutsideOfInput: () => void;
    onPressOutsideOfSuggestions: () => void;
    onSuggestionFocus: () => void;
    onSuggestionTouchStart: () => void;
    isAutoCompleteFocused: boolean;
    onFocusInputFromValueDeletion: () => void;
    onInputAreaPress: () => void;
    onSuggestionSelected: () => void;
};

const BLUR_DELAY = 200;
const AUTO_BLUR_FOCUS_DELAY = 50;

export const useAutoCompleteInputEventOrchestration = ({
    onBlurFromParent,
    onFocusFromParent,
    textInput,
}: UseAutoCompleteInputEventOrchestrationArgs): UseAutoCompleteInputEventOrchestrationReturnValue => {
    const [state, transition] = useStateMachine({
        initial: 'unFocused',
        states: {
            unFocused: {
                on: {
                    INPUT_FOCUSED: 'inputFocused',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                },
                effect: ({ event }: { event: { type: string } }) => {
                    const isInitial = event.type === '$$initial';
                    if (isInitial) return;

                    const timeout = setTimeout(() => {
                        textInput?.blur();
                        onBlurFromParent?.();
                    }, AUTO_BLUR_FOCUS_DELAY);

                    return () => {
                        clearTimeout(timeout);
                    };
                },
            },
            inputFocused: {
                on: {
                    INPUT_BLURRED: 'pendingInputBlur',
                    SUGGESTION_FOCUSED: 'suggestionFocused',
                    PRESS_OUTSIDE_OF_INPUT: 'pendingInputBlur',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                },
                effect: () => {
                    textInput?.focus();
                    onFocusFromParent?.();
                },
            },
            pendingInputBlur: {
                on: {
                    INPUT_BLUR_DELAY_LAPSED: 'autoCompleteBlurred',
                    SUGGESTION_FOCUSED: 'suggestionFocused',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                },
                effect: ({ send }: { send: (event: string) => void }) => {
                    textInput?.blur();
                    const timeout = setTimeout(() => {
                        send('INPUT_BLUR_DELAY_LAPSED');
                    }, BLUR_DELAY);

                    return () => {
                        clearTimeout(timeout);
                    };
                },
            },
            suggestionFocused: {
                on: {
                    PRESS_OUTSIDE_OF_SUGGESTIONS: 'pendingSuggestionBlur',
                    INPUT_FOCUSED: 'inputFocused',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                    SUGGESTION_SELECTED: 'autoCompleteBlurred',
                },
                effect: () => {
                    onFocusFromParent?.();
                },
            },
            pendingSuggestionBlur: {
                on: {
                    SUGGESTION_BLUR_DELAY_LAPSED: 'autoCompleteBlurred',
                    INPUT_FOCUSED: 'inputFocused',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                },
                effect: ({ send }: { send: (event: string) => void }) => {
                    const timeout = setTimeout(() => {
                        send('SUGGESTION_BLUR_DELAY_LAPSED');
                    }, BLUR_DELAY);

                    return () => {
                        clearTimeout(timeout);
                    };
                },
            },
            autoCompleteBlurred: {
                on: {
                    TRANSITION_COMPLETE: 'unFocused',
                    FOCUS_AUTO_COMPLETE_TEXT_INPUT: 'pendingFocusAutoCompleteTextInput',
                },
                effect: ({ send }: { send: (event: string) => void }) => {
                    send('TRANSITION_COMPLETE');
                },
            },
            pendingFocusAutoCompleteTextInput: {
                on: {
                    INPUT_FOCUSED: 'inputFocused',
                },
                effect: () => {
                    const timeout = setTimeout(() => {
                        transition('INPUT_FOCUSED');
                    }, AUTO_BLUR_FOCUS_DELAY);
                    return () => {
                        clearTimeout(timeout);
                    };
                },
            },
        },
    });

    return {
        onInputFocus: () => {
            transition('INPUT_FOCUSED');
        },
        onInputBlur: () => {
            transition(`INPUT_BLURRED`);
        },
        onPressOutsideOfInput: () => {
            transition(`PRESS_OUTSIDE_OF_INPUT`);
        },
        onPressOutsideOfSuggestions: () => {
            transition(`PRESS_OUTSIDE_OF_SUGGESTIONS`);
        },
        onSuggestionFocus: () => {
            transition(`SUGGESTION_FOCUSED`);
        },
        onSuggestionTouchStart: () => {
            transition(`SUGGESTION_FOCUSED`);
        },
        onFocusInputFromValueDeletion: () => {
            transition('FOCUS_AUTO_COMPLETE_TEXT_INPUT');
        },
        onInputAreaPress: () => {
            transition('FOCUS_AUTO_COMPLETE_TEXT_INPUT');
        },
        onSuggestionSelected: () => {
            transition('SUGGESTION_SELECTED');
        },
        isAutoCompleteFocused: state.value !== 'unFocused',
    };
};
